import React, { useState, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

// 🔔 Alert Notification Context
export const notifContext = React.createContext();

/**
 * AlertNotifProvider
 */
export const NotifProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadModule, setUnreadModule] = useState({
    message: [],
    project: [],
  });

  // Message Notification
  const [newMessage, setNewMessage] = useState(null);
  const [newMessageRead, setNewMessageRead] = useState(null);

  const audioRef = useRef(null);
  // ➕ Add new alert
  const addAlert = useCallback(({ title, html, width = 300, path }) => {
    const id = Date.now() + Math.random();
    setAlerts((prev) => [...prev, { id, title, html, width, path }]);

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, []);
  const addAudioAlert = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  // ❌ Remove alert
  const removeAlert = useCallback((id) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // 💡 Memoize context value
  const contextValue = useMemo(
    () => ({
      alerts,
      addAlert,
      removeAlert,
      addAudioAlert,
      unreadModule,
      setUnreadModule,
      newMessage,
      setNewMessage,
      newMessageRead,
      setNewMessageRead,
    }),
    [alerts, addAlert, removeAlert, unreadModule, newMessage, newMessageRead]
  );

  return (
    <notifContext.Provider value={contextValue}>
      {children}
      <audio
        ref={audioRef}
        src="/sounds/notif.mp3"
        preload="auto"
        style={{ display: "none" }}
      />
    </notifContext.Provider>
  );
};

NotifProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/* ------------------------
   ALERT UI COMPONENTS
------------------------ */

const AlertNotifContainer = ({ alerts, removeAlert }) => (
  <div
    style={{
      position: "fixed",
      top: 12,
      right: 12,
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      alignItems: "flex-end",
      pointerEvents: "none",
    }}
  >
    {alerts.map((alert) => (
      <AlertItem key={alert.id} alert={alert} removeAlert={removeAlert} />
    ))}
  </div>
);

AlertNotifContainer.propTypes = {
  alerts: PropTypes.array.isRequired,
  removeAlert: PropTypes.func.isRequired,
};

const AlertItem = ({ alert, removeAlert }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (alert.path) navigate(alert.path);
    removeAlert(alert.id);
  };

  return (
    <div
      className="alert-notif"
      onClick={handleClick}
      style={{
        width:
          typeof alert.width === "number" ? `${alert.width}px` : alert.width,
        background: "#fff",
        borderRadius: 10,
        boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
        padding: "12px 16px",
        cursor: "pointer",
        transition: "transform 0.2s ease, opacity 0.2s ease",
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <strong style={{ color: "#333" }}>{alert.title}</strong>
        <button
          className="alert-close"
          onClick={(e) => {
            e.stopPropagation();
            removeAlert(alert.id);
          }}
          style={{
            border: "none",
            background: "transparent",
            fontSize: 18,
            cursor: "pointer",
            color: "#777",
            lineHeight: 1,
          }}
          aria-label="Close alert"
        >
          ×
        </button>
      </div>
      {alert.html && (
        <div
          style={{ marginTop: 4, color: "#555", fontSize: 14 }}
          dangerouslySetInnerHTML={{ __html: alert.html }}
        />
      )}
    </div>
  );
};

AlertItem.propTypes = {
  alert: PropTypes.object.isRequired,
  removeAlert: PropTypes.func.isRequired,
};
